<template>
  <div class="content">
    <Background>
      <Loading v-if="loading" isCenter isFull />
      <FriendlierCard v-else>
        <div class="padded-block">
          <div class="logo">
            <LocaleAwareLogo variant="primary" color="cloud" />
          </div>

          <div class="charity-selection-section">
            <div class="title">Choose a Charity to Donate To</div>
            <div class="subtitle">Select from our recommended local charities</div>

            <div v-if="charities.length > 0" class="charities-grid">
              <div v-for="charity in charities" :key="charity.id" class="charity-card" @click="selectCharity(charity)">
                <div class="charity-logo">
                  <img :src="charity.logo || FriendlierPrimaryCloud" :alt="charity.charityName || 'Charity logo'" />
                </div>
                <div class="charity-info">
                  <h3>{{ charity.charityName || charity.name || 'Charity' }}</h3>
                  <p>{{ charity.descriptionLang?.en || charity.description || '' }}</p>
                  <div class="charity-website">
                    <a v-if="charity.websiteUrl" :href="charity.websiteUrl" target="_blank"
                      rel="noopener noreferrer">Learn More</a>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="no-charities">
              <p>No charities available at the moment. Please try again later.</p>
            </div>

            <div class="back-button">
              <button class="button secondary-button" @click="goBack">
                Back to Return
              </button>
            </div>
          </div>
        </div>
      </FriendlierCard>
    </Background>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";

import { FriendlierPrimaryCloud } from "@/revamp/assets/images";
import {
  Loading,
  Background,
  FriendlierCard,
  LocaleAwareLogo,
} from "@/revamp/components";
import { useFirebase } from "@/revamp/composables";
import { logger, toQueryString } from "@/revamp/utils";

const loading = ref(true);
const charities = ref([]);

const APIService = useFirebase();
const Router = useRouter();
const route = useRoute();

let email = "";
let codeDisplay = "";
let codeUrl = "";
let source = "";
let platform = "";
let codeForm = "";
let pin = "";

onMounted(async () => {
  email = toQueryString(route.query.email);
  codeDisplay = toQueryString(route.query.codeDisplay);
  codeUrl = toQueryString(route.query.codeUrl);
  source = toQueryString(route.query.source);
  platform = toQueryString(route.query.platform);
  codeForm = toQueryString(route.query.codeForm);
  pin = toQueryString(route.query.pin);
  window.history.replaceState({}, document.title, "/charity-selection");
  if (!codeDisplay && !codeUrl) {
    logger.error("CharitySelection: Missing codeDisplay and codeUrl in query parameters");
    Router.replace({ name: "Return" });
    return;
  }
  if (!source || !platform || !codeForm) {
    logger.error("CharitySelection: Missing source, platform, or codeForm in query parameters");
    Router.replace({ name: "Return" });
    return;
  }
  try {
    charities.value = await APIService.listCharitiesQuickClaim(codeDisplay, codeUrl);
    if (!charities.value) throw new Error("No response from charity API");
  } catch (error) {
    logger.error(`CharitySelection.loadCharities: Failed to load charities. ${error.toString()}`);
    charities.value = [];
  }
  loading.value = false;
});


function selectCharity(charity) {
  // Navigate to donation confirmation page
  Router.replace({
    name: "DonationConfirmation",
    query: {
      charity: encodeURIComponent(JSON.stringify(charity)),
      codeDisplay: codeDisplay || undefined,
      codeUrl: codeUrl || undefined,
      source: source || undefined,
      codeForm: codeForm || undefined,
      platform: platform || undefined,
      email: email || undefined,
      pin: pin || undefined
    }
  });
}

function goBack() {
  Router.replace({
    name: "Return",
    query: { code: codeDisplay || undefined, url: codeUrl || undefined, email: email || undefined, pin: pin || undefined }
  });
}
</script>

<style scoped lang="scss">
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";
@import "@/revamp/assets/scss/variables.scss";

.charity-selection-section {
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-top: 15px;
  padding-bottom: 30px;

  .title {
    font-size: 1.5rem;
    color: $sunflower;
    width: 100%;
    text-align: center;
    font-weight: bold;
  }

  .subtitle {
    font-size: 1rem;
    color: $cloud;
    width: 100%;
    margin-bottom: 30px;
    text-align: center;
  }
}

.charities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.charity-card {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    border-color: $sunflower;
  }

  .charity-logo {
    text-align: center;
    margin-bottom: 15px;

    img {
      max-height: 80px;
      max-width: 150px;
      object-fit: contain;
    }
  }

  .charity-info {
    text-align: center;

    h3 {
      color: $denim;
      font-size: 1.2rem;
      margin-bottom: 10px;
      font-weight: bold;
    }

    p {
      color: black;
      font-size: 0.9rem;
      line-height: 1.4;
      margin-bottom: 15px;
    }

    .charity-website {
      a {
        color: $sunflower;
        text-decoration: none;
        font-weight: bold;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.no-charities {
  text-align: center;
  padding: 40px;

  p {
    color: $cloud;
    font-size: 1.1rem;
  }
}

.back-button {
  text-align: center;
  margin-top: 20px;

  .button {
    padding: 12px 24px;
    border-radius: 10px;
    border: none;
    font-weight: bold;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .secondary-button {
    background-color: $cloud;
    color: $denim;

    &:hover {
      background-color: darken($cloud, 10%);
    }
  }
}

.logo {
  width: 100%;
  text-align: center;
  margin-bottom: 15px;
}

.logo img {
  max-width: 150px;
  height: auto;
}
</style>